<script setup>
// 3rd.
import { computed, watch, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import VueScrollTo from 'vue-scrollto';
import queryString from 'query-string';
// Store.
import useProductFinderStore from '../../stores/productFinder';
// Components.
import DialogModal from '../elements/DialogModal';
import Loading from '../elements/Loading';
import Product from './Product';
import ProductFinderFilters from './ProductFinderFilters';
// Utils.
import copyToClipboard from '../../utils/copyToClipboard';
import scrollLock from '../../utils/scrollLock';
import { ORGANIC_FARMING_ID, VUESCROLLTO_OPTIONS } from '../../utils/constants';
import { default as t } from '../../translations/translationStrings';
import { isMobile } from '../../utils/isMobile';

// Data.
const isLoading = ref(true);
const {
  selectedCrops,
  selectedApplications,
  selectedTargetAreas,
  sustainableApplicationFilters,
  sustainableProductionFilters,
  showOnlyOrganic,
  transitionName,
} = storeToRefs(useProductFinderStore());
const productsDisplayLimit = ref(6);
const showCopyAndShareModal = ref(false);
const copyAndShareModal = ref(null);

// Props.
const props = defineProps({
  lang: {
    type: String,
    required: true,
    default: '',
  },
  title: {
    type: String,
    required: true,
    default: '',
  },
  noResults: {
    type: String,
    required: true,
    default: t.noResults,
  },
});

// Computed & store getters.
const { getFilteredProducts } = useProductFinderStore();
const filteredProducts = computed(() => getFilteredProducts());

// EN Global has no prefix and would return 404.
const requestLang = computed(() => {
  return props.lang === 'en' ? '' : props.lang;
});

const pageTitle = computed(() => {
  // wrap the last word in <span>, bc it needs diff styling
  return props.title.replace(/\w+$/g, '<span>$&</span>');
});

// Watch for results count change and scroll to #product-finder on change.
watch(filteredProducts, () => {
  if (!isMobile()) {
    VueScrollTo.scrollTo('#product-finder-products', VUESCROLLTO_OPTIONS);
  }
});

// Methods & store actions.
const openModal = () => {
  showCopyAndShareModal.value = true;
  copyAndShareModal.value.openDialogModal();
};

const parseUrl = () => {
  const parsed = queryString.parse(window.location.search);

  // Crops.
  if (parsed && parsed.crops && parsed.crops.length) {
    selectedCrops.value = parsed.crops.split(',').map((val) => Number(val));
  }

  // Applications.
  if (parsed && parsed.applications && parsed.applications.length) {
    selectedApplications.value = parsed.applications
      .split(',')
      .map((val) => Number(val));
  }

  // Targets.
  if (parsed && parsed.targets && parsed.targets.length) {
    selectedTargetAreas.value = parsed.targets
      .split(',')
      .map((val) => Number(val));

    // Check for organic farming switch value.
    if (selectedTargetAreas.value.includes(ORGANIC_FARMING_ID)) {
      showOnlyOrganic.value = true;
    }
  }

  // Sustainability fields.
  if (parsed && parsed.sustainableApplication) {
    parsed.sustainableApplication.split(',').forEach((key) => {
      sustainableApplicationFilters.value[key] = true;
    });
  }
  if (parsed && parsed.sustainableProduction) {
    parsed.sustainableProduction.split(',').forEach((key) => {
      sustainableProductionFilters.value[key] = true;
    });
  }
};

// This cannot be a computed prop because it wouldn't be reactive.
const getFullUrl = () => {
  return window.location.href;
};

// Load more products on scroll.
const handleScroll = () => {
  const productFinder = document.querySelector('#product-finder');
  const bottomOfApplication =
    window.scrollY + window.innerHeight + 100 >
    productFinder.offsetHeight + productFinder.offsetTop;
  if (bottomOfApplication) {
    transitionName.value = 'products-fade-in';
    productsDisplayLimit.value += 4;
  }
};

// Mounted.
onMounted(async () => {
  // Make sure page doesn't start from the middle of the results.
  window.scrollTo(0, 0);
  scrollLock.lock();

  // Load products data.
  try {
    const { initStore } = useProductFinderStore();
    await initStore(requestLang.value);

    // Parse URL and select filters based on query params.
    parseUrl();
  } catch (error) {
    // Todo: display a styled error message?
    console.error(error);
  } finally {
    // Disable loading and allow scrolling.
    isLoading.value = false;
    scrollLock.unlock();

    // Attach event listeners.
    window.addEventListener('scroll', handleScroll);
  }
});
</script>

<template>
  <Transition name="product-finder-loading">
    <!-- Loading. -->
    <Loading v-if="isLoading" />
    <!-- Results. -->
    <div v-else class="product-finder">
      <div class="product-finder__title">
        <h1 class="title-custom" v-html="pageTitle"></h1>
        <!-- Copy & share modal trigger. -->
        <button class="product-finder__share" @click="openModal">
          {{ t.shareModal.copyAndShareLabel }}
          <span></span>
        </button>
      </div>

      <!-- Filters. -->
      <ProductFinderFilters :lang="requestLang" />

      <!-- Results. -->
      <TransitionGroup
        v-if="filteredProducts.length > 0"
        id="product-finder-products"
        :name="transitionName"
        tag="ul"
        class="product-finder__products"
        :style="{
          '--total': filteredProducts.slice(0, productsDisplayLimit).length,
        }"
        appear>
        <Product
          v-for="(product, index) in filteredProducts.slice(
            0,
            productsDisplayLimit,
          )"
          :key="`product${product.id}`"
          :style="{ '--i': Math.round(index % 3) }"
          :product="product" />
      </TransitionGroup>

      <!-- Or else show no results text. -->
      <div v-else class="product-finder__no-products" v-html="noResults"></div>

      <!-- Copy & share modal. -->
      <DialogModal
        v-modal="showCopyAndShareModal"
        modal-type="product-finder"
        ref="copyAndShareModal">
        <!-- Modal header. -->
        <template #header>
          <h3>{{ t.shareModal.copyAndShareModalTitle }}</h3>
        </template>

        <!-- Modal content. -->
        <template #content>
          <p>
            {{ t.shareModal.copyAndShareModalText }}
          </p>
          <div class="product-finder__copy-widget">
            <!-- Re-use Drupal classes for styling. -->
            <form @submit.prevent>
              <div class="form-item">
                <input
                  id="copy&share"
                  type="text"
                  :value="getFullUrl()"
                  readonly />
                <label for="copy&share">{{ t.shareModal.quickLink }}</label>
                <button
                  @click="copyToClipboard(getFullUrl(), $event.currentTarget)">
                  <i class="far fa-copy"></i>
                  <span class="tooltip">
                    {{ t.shareModal.copyFeedback }}
                  </span>
                </button>
              </div>
            </form>
            <!-- Email & WhatsApp links. -->
            <div class="product-finder__share-links">
              <a
                :href="`mailto:?subject=${
                  t.shareModal.emailShareSubject
                }&body=${getFullUrl()}`"></a>
              <a
                :href="`https://wa.me/?text=${
                  t.shareModal.whatsappShareText
                } ${getFullUrl()}`">
              </a>
            </div>
          </div>
        </template>
      </DialogModal>
    </div>
  </Transition>
</template>
